import { useState } from 'react';
import {
  Dialog,
  Button,
  Tooltip,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { MIconButton } from '@/@material-extend';
import { makeStyles } from '@mui/styles';

import CONFIG from '@/config';
import { NOTE_ACTIONS, StaffNote } from './Types';
import { getRestHeaders } from '@/common/utils/api';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { formDataInstance } from '@/common/axios';
import { getErrorDetails } from './ApiErrorLookup';

const useStyles: any = makeStyles(() => ({
  root: {},
  message: {
    fontWeight: 400
  },
  actions: {
    '& button': {
      backgroundColor: '#055577',
      color: '#FFFFFF',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#042E43'
      }
    },
    '& button:first-child': {
      marginRight: 8,
      background: '#FAFAFA',
      border: 'none',
      color: '#000',
      '&:hover': {
        backgroundColor: '#FAFAFA'
      }
    }
  }
}));

export interface DeleteNoteApiResponse {
  data: {
    data: {
      note: StaffNote;
      success?: boolean;
    };
  };
}

export interface DeleteNoteModalProps {
  isOpen: boolean;
  isComment?: boolean;
  commentId?: number;
  note: StaffNote;
  setIsOpen: (isOpen: boolean) => void;
  actionCallback: (
    note: StaffNote,
    action: keyof typeof NOTE_ACTIONS,
    data: any
  ) => void;
}

export const apiSuffix = '/staff/user-note';

const DeleteNoteConfirmModal = ({
  isOpen = false,
  isComment = false,
  commentId,
  note,
  setIsOpen,
  actionCallback
}: DeleteNoteModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const classes = useStyles();

  const deleteNote = () => {
    setLoading(true);
    const entityName = `Note${isComment ? ' comment' : ''}`;
    formDataInstance
      .delete(
        `${CONFIG.USERS_API_URL}${apiSuffix}?noteId=${
          isComment ? commentId : note.id
        }`,
        {
          headers: getRestHeaders()
        }
      )
      .then((response: DeleteNoteApiResponse) => {
        if (response.data.data.success) {
          actionCallback(
            note,
            isComment ? NOTE_ACTIONS.REMOVE_COMMENT : NOTE_ACTIONS.REMOVE_NOTE,
            commentId
          );
          enqueueSnackbar(`Successfully Deleteed ${entityName}.`, {
            variant: 'success',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          });
          setIsOpen(false);
        }
      })
      .catch(error => {
        enqueueSnackbar(
          getErrorDetails(
            `There was a problem Deleteing ${entityName}.`,
            error
          ),
          {
            variant: 'error',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          }
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog fullWidth open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 15px 10px 24px',
          justifyContent: 'space-between',
          fontSize: 16,
          fontWeight: 600
        }}
      >
        Delete {isComment ? 'Comment' : 'Note'}
        <Tooltip arrow title="Close">
          <IconButton onClick={() => setIsOpen(false)} size="large">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent className={classes.message}>
        Are you sure you want to delete this {isComment ? 'comment' : 'note'}?
        This action cannot be undone.
        {isComment ? '' : '  Any comments will also be deleted.'}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="outlined" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={deleteNote}
          loading={loading}
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteNoteConfirmModal;
